import type { CountryCode } from 'libphonenumber-js';
import { parsePhoneNumberWithError } from 'libphonenumber-js';

/**
 * Validates a phone number.
 *
 * @param phoneNumber - The phone number string to validate.
 * @param countryCode - Optional ISO country code (e.g., 'US', 'LT').
 *                      If provided, the number is validated specifically for that country.
 *                      If not provided, it's validated as an international number.
 * @returns boolean - True if the phone number is valid.
 */
export const validatePhoneNumber = (
  phoneNumber: string,
  countryCode?: string,
): boolean => {
  if (!phoneNumber) return false;

  try {
    // Ensure we have a + and only digits
    const digits = phoneNumber.replace(/\D/g, '');
    const normalizedNumber = `+${digits}`;

    const parsedNumber = parsePhoneNumberWithError(
      normalizedNumber,
      countryCode?.toUpperCase() as CountryCode,
    );

    if (countryCode) {
      // If country specified, check if it matches and is valid for that country
      return (
        parsedNumber.isValid() &&
        parsedNumber.country === (countryCode.toUpperCase() as CountryCode)
      );
    }

    return parsedNumber.isValid();
  } catch (error) {
    return false;
  }
};
